import { Activity } from '@models/activity.model';
import { ActivityHeader } from '../ActivityHeader';
import { TaskName } from '../TaskName';
import { formatActivityTime } from '@utils/formatDate';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Card } from '@components/atoms/Card';
import { TaskStatus } from '@models/task.model';
import { getStatusColor } from '@utils/statusColors';
import { getTranslatedTaskStatusLabel } from '@utils/options';
import Alert from '@components/atoms/Alert';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ErrorIcon from '@mui/icons-material/Error';
import { useTranslation } from '@hooks/useTranslation';

export const ActivityTaskStatus = ({
  activity,
  handleOpenTaskDetail,
}: {
  activity: Activity;
  handleOpenTaskDetail?: (taskId: string) => void;
}) => {
  const tActivityCards = useTranslation('activityCards');
  const tTaskDetail = useTranslation('taskDetail');
  const previousStatus = activity.statusHistory?.previousStatus;
  const newStatus = activity.statusHistory?.newStatus || activity.newStatus;

  const statusEnum = newStatus
    ? TaskStatus[newStatus as keyof typeof TaskStatus]
    : undefined;

  const taskStatusTextMap: Record<TaskStatus, string> = {
    [TaskStatus.ACTIVE]: tActivityCards.taskStatus.statusActive,
    [TaskStatus.INACTIVE]: tActivityCards.taskStatus.statusInactive,
    [TaskStatus.COMPLETE]: tActivityCards.taskStatus.statusComplete,
    [TaskStatus.REJECTED]: tActivityCards.taskStatus.statusRejected,
    [TaskStatus.CANCELED]: tActivityCards.taskStatus.statusCanceled,
    [TaskStatus.INTERNAL_APPROVAL]: tActivityCards.taskStatus.statusInternalApproval,
    [TaskStatus.CLIENT_APPROVAL]: tActivityCards.taskStatus.statusClientApproval,
    [TaskStatus.DELEGATION_CANCELED]: tActivityCards.taskStatus.statusDelegationCanceled,
  };

  const statusText = statusEnum ? taskStatusTextMap[statusEnum] : '';

  // Sprint 28 change request (story-144 History, defect fix). Was rendering the previous/new
  // status chips through `formatStatusDisplayName` (`@utils/statusColors`), a pure title-casing
  // function with no locale awareness (`ACTIVE` -> `Active`) — a Spanish user saw English chip
  // text. `getTranslatedTaskStatusLabel` (`@utils/options`) is the same helper the review's
  // Critical #1 fix wired into `TaskStatusOptions`' other call sites; reusing it here keeps a
  // single translated source for task-status labels instead of a second map.
  const StatusChip = ({ status }: { status: string }) => (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-white"
      style={{ backgroundColor: getStatusColor(status) }}>
      {getTranslatedTaskStatusLabel(status, tTaskDetail)}
    </span>
  );

  const rightLabel = (
    <div
      className="flex items-center gap-2"
      data-testid="activity-task-status-change"
      data-previous-status={previousStatus || undefined}
      data-new-status={newStatus || undefined}>
      {previousStatus ? (
        <>
          <StatusChip status={previousStatus} />
          <ArrowForwardIcon fontSize="small" />
          {newStatus && <StatusChip status={newStatus} />}
        </>
      ) : (
        <>
          <CheckCircleOutlineIcon fontSize="small" />
          <p className="text-sm">{statusText}</p>
        </>
      )}
      {activity.createdAt && (
        <p className="text-sm text-gray-500">
          {`- ${formatActivityTime(activity.createdAt)}`}
        </p>
      )}
    </div>
  );

  return (
    <Card data-testid="activity-task-status-card">
      <ActivityHeader activity={activity} rightLabel={rightLabel} />

      <div className="mt-4 space-y-3">
        {previousStatus || newStatus ? (
          <Alert className="h-full">
            <div className="space-y-2">
              <div className="flex gap-1 items-baseline">
                <p className="text-sm text-neutral-800">{tActivityCards.projectLabel}</p>
                <p className="text-sm text-neutral-800 font-semibold capitalize">
                  {activity.project?.name || tActivityCards.notAvailable}
                </p>
              </div>

              <TaskName
                task={activity.task}
                size="sm"
                taskId={activity.taskId || activity.task?.taskId}
                onOpenTask={handleOpenTaskDetail}
              />

              {newStatus === 'REJECTED' && activity.statusHistory?.reason && (
                <Alert className="h-full" variant="error" icon={<ErrorIcon />}>
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-red-400"
                        viewBox="0 0 20 20"
                        fill="currentColor">
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3 flex-1">
                      <h4 className="text-sm font-medium text-red-800">
                        {tActivityCards.taskStatus.rejectionReason}
                      </h4>
                      <p className="mt-1 text-sm text-red-700">
                        {activity.statusHistory.reason}
                      </p>
                    </div>
                  </div>
                </Alert>
              )}

              {/* Any other change can carry a note too — e.g. a middle company in a delegation
                  chain approving its subcontractor's work records "Approved and sent to <client>"
                  without its own status changing, and this note is the only way the feed shows it. */}
              {newStatus !== 'REJECTED' && activity.statusHistory?.reason && (
                <p
                  className="text-sm text-neutral-600 dark:text-neutral-400"
                  data-testid="activity-task-status-note">
                  {activity.statusHistory.reason}
                </p>
              )}
            </div>
          </Alert>
        ) : null}
      </div>
    </Card>
  );
};
